import { useEffect, useRef, useCallback } from 'react';
import {
  HubConnectionState,
} from '@microsoft/signalr';
import { logger } from '@/lib/logger';
import {
  getOrCreateHubConnection,
  ensureHubConnected,
  incrementSubscribers,
  decrementAndMaybeDisconnect,
  getSharedConnection,
} from './hubConnection';

export interface DocumentListChangedEvent {
  workspaceId: string;
  changeType: 'DocumentUploaded' | 'DocumentDeleted' | 'StatusChanged';
  documentId: string;
  timestamp: string;
}

interface WorkspaceHubCallbacks {
  onDocumentListChanged?: (data: DocumentListChangedEvent) => void;
}

/**
 * Hook to subscribe to workspace-level document list changes via SignalR.
 * Enables cross-browser synchronization: when Browser A uploads/deletes a
 * document, Browser B gets notified and can refresh its document list.
 *
 * @param workspaceId - The workspace to subscribe to (null = disconnected)
 * @param callbacks   - Event handlers for workspace-level events
 */
export function useWorkspaceHub(
  workspaceId: string | null,
  callbacks: WorkspaceHubCallbacks,
) {
  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;

  const subscribedWsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!workspaceId) return;

    let cancelled = false;
    const connection = getOrCreateHubConnection();
    incrementSubscribers();

    const onDocumentListChanged = (data: DocumentListChangedEvent) => {
      if (!cancelled) cbRef.current.onDocumentListChanged?.(data);
    };

    connection.on('DocumentListChanged', onDocumentListChanged);

    // Connect + subscribe to workspace group
    ensureHubConnected(connection)
      .then(() => {
        if (!cancelled && connection.state === HubConnectionState.Connected) {
          return connection.invoke('SubscribeToWorkspace', workspaceId);
        }
      })
      .then(() => {
        if (!cancelled) {
          subscribedWsRef.current = workspaceId;
          logger.debug('WorkspaceHub', `Subscribed to workspace ${workspaceId}`);
        }
      })
      .catch((err) => {
        if (!cancelled) logger.error('WorkspaceHub', 'Error', err);
      });

    return () => {
      cancelled = true;

      connection.off('DocumentListChanged', onDocumentListChanged);

      if (
        subscribedWsRef.current &&
        connection.state === HubConnectionState.Connected
      ) {
        connection
          .invoke('UnsubscribeFromWorkspace', subscribedWsRef.current)
          .catch(() => {});
        subscribedWsRef.current = null;
      }

      decrementAndMaybeDisconnect();
    };
  }, [workspaceId]);

  const conn = getSharedConnection();
  return {
    subscribe: useCallback((wsId: string) => {
      if (!conn || conn.state !== HubConnectionState.Connected)
        return Promise.reject('No connection');
      return conn.invoke('SubscribeToWorkspace', wsId);
    }, [conn]),
    unsubscribe: useCallback((wsId: string) => {
      if (!conn || conn.state !== HubConnectionState.Connected)
        return Promise.reject('No connection');
      return conn.invoke('UnsubscribeFromWorkspace', wsId);
    }, [conn]),
  };
}
